import { useRequiredContext } from "@redotech/react-util/context";
import BarChart01Icon from "@redotech/redo-icons/bar-chart-01.svg";
import BarChart01FilledIcon from "@redotech/redo-icons/bar-chart-01_filled.svg";
import BookOpen01Icon from "@redotech/redo-icons/book-open-01.svg";
import BookOpen01FilledIcon from "@redotech/redo-icons/book-open-01_filled.svg";
import CalendarIcon from "@redotech/redo-icons/calendar.svg";
import CalendarFilledIcon from "@redotech/redo-icons/calendar_filled.svg";
import CallBellIcon from "@redotech/redo-icons/call-bell.svg";
import CallBellFilledIcon from "@redotech/redo-icons/call-bell_filled.svg";
import DashboardIcon from "@redotech/redo-icons/dashboard.svg";
import DashboardFilledIcon from "@redotech/redo-icons/dashboard_filled.svg";
import MessageChatCircleIcon from "@redotech/redo-icons/message-chat-circle.svg";
import MessageChatCircleFilledIcon from "@redotech/redo-icons/message-chat-circle_filled.svg";
import PlayCircleIcon from "@redotech/redo-icons/play-circle.svg";
import PlayCircleFilledIcon from "@redotech/redo-icons/play-circle_filled.svg";
import ZapIcon from "@redotech/redo-icons/zap.svg";
import ZapFilledIcon from "@redotech/redo-icons/zap_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { shopperAgentEnabled } from "../../shopper-agent-enabled";

export function useShopperAgentNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);

  const isEnabled = shopperAgentEnabled(team);
  if (!isEnabled) {
    return null;
  }

  const primaryTab: LinkPrimaryTab = {
    id: "shopper-agent",
    hasNotification: false,
    UnfilledIcon: CallBellIcon,
    FilledIcon: CallBellFilledIcon,
    type: "link",
    pathRoot: routes.shopperAgentHref,
    href: routes.shopperAgentHref,
    friendlyName: "Commerce Agent",
  };

  const secondaryNavigationData: SecondaryNavigationData | undefined = team
    .settings.shopperAgent?.enabled
    ? {
        id: "shopper-agent",
        name: "Commerce Agent",
        NavContent: SecondaryShopperAgentNavigation,
      }
    : undefined;

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: team.settings.shopperAgent?.enabled === true,
  };
}

const SecondaryShopperAgentNavigation = memo(
  function SecondaryShopperAgentNavigation() {
    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: "Commerce Agent",
    });

    return (
      <TreeContext.Provider value={nodeId}>
        <ShopperAgentDashboardItem />
        <ShopperAgentAnalyticsItem />
        <ShopperAgentCalendarItem />
        <ShopperAgentConversationsItem />
        <ShopperAgentKnowledgeItem />
        <ShopperAgentPlaybookItem />
        <ShopperAgentSandboxItem />
      </TreeContext.Provider>
    );
  },
);

const ShopperAgentDashboardItem = memo(function ShopperAgentDashboardItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.shopperAgentHref }}
      FilledIcon={DashboardFilledIcon}
      friendlyName="Dashboard"
      id="shopper-agent-dashboard"
      UnfilledIcon={DashboardIcon}
    />
  );
});

const ShopperAgentAnalyticsItem = memo(function ShopperAgentAnalyticsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getShopperAgentSubRoute("analytics"),
      }}
      FilledIcon={BarChart01FilledIcon}
      friendlyName="Analytics"
      id="shopper-agent-analytics"
      UnfilledIcon={BarChart01Icon}
    />
  );
});

const ShopperAgentCalendarItem = memo(function ShopperAgentCalendarItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getShopperAgentSubRoute("calendar"),
      }}
      FilledIcon={CalendarFilledIcon}
      friendlyName="Calendar"
      id="shopper-agent-calendar"
      UnfilledIcon={CalendarIcon}
    />
  );
});

const ShopperAgentConversationsItem = memo(
  function ShopperAgentConversationsItem() {
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getShopperAgentSubRoute("conversations"),
        }}
        FilledIcon={MessageChatCircleFilledIcon}
        friendlyName="Conversations"
        id="shopper-agent-conversations"
        UnfilledIcon={MessageChatCircleIcon}
      />
    );
  },
);

const ShopperAgentKnowledgeItem = memo(function ShopperAgentKnowledgeItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getShopperAgentSubRoute("knowledge"),
      }}
      FilledIcon={BookOpen01FilledIcon}
      friendlyName="Knowledge"
      id="shopper-agent-knowledge"
      UnfilledIcon={BookOpen01Icon}
    />
  );
});

const ShopperAgentPlaybookItem = memo(function ShopperAgentPlaybookItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{
        type: "link",
        href: routes.getShopperAgentSubRoute("playbook"),
      }}
      FilledIcon={ZapFilledIcon}
      friendlyName="Playbook"
      id="shopper-agent-playbook"
      UnfilledIcon={ZapIcon}
    />
  );
});

const ShopperAgentSandboxItem = memo(function ShopperAgentSandboxItem() {
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.getShopperAgentSubRoute("sandbox") }}
      FilledIcon={PlayCircleFilledIcon}
      friendlyName="Sandbox"
      id="shopper-agent-sandbox"
      UnfilledIcon={PlayCircleIcon}
    />
  );
});
